
import { useState, useEffect } from 'react';
import { QuizQuestion } from '../../../mocks/quiz-autoconhecimento';

interface QuizQuestionsProps {
  questions: QuizQuestion[];
  onComplete: (answers: Record<number, string>) => void;
}

export default function QuizQuestions({ questions, onComplete }: QuizQuestionsProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');

  const current = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;

  useEffect(() => {
    const saved = answers[current.id];
    setSelectedOption(saved || null);
  }, [currentIndex, current.id, answers]);

  const handleSelect = (profile: string) => {
    setSelectedOption(profile);
    setAnswers((prev) => ({ ...prev, [current.id]: profile }));
  };

  const handleNext = () => {
    if (!selectedOption) return;
    if (currentIndex === questions.length - 1) {
      onComplete(answers);
      return;
    }
    setDirection('next');
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex((prev) => prev + 1);
      setIsAnimating(false);
    }, 300);
  };

  const handlePrev = () => {
    if (currentIndex === 0) return;
    setDirection('prev');
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex((prev) => prev - 1);
      setIsAnimating(false);
    }, 300);
  };

  return (
    <section className="min-h-screen bg-gradient-to-b from-luxury-black via-luxury-dark to-luxury-black flex items-center py-24">
      <div className="container mx-auto px-6 lg:px-16">
        <div className="max-w-2xl mx-auto">
          {/* Barra de progresso */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-3">
              <span className="font-['Montserrat'] text-xs text-gold-400/60 tracking-wider uppercase">
                Pergunta {currentIndex + 1} de {questions.length}
              </span>
              <span className="font-['Montserrat'] text-xs text-gold-400/60 tracking-wider">
                {Math.round(progress)}%
              </span>
            </div>
            <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-gold-500 to-gold-400 rounded-full transition-all duration-700 ease-out"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>

          {/* Pergunta */}
          <div
            className={`transition-all duration-300 ${
              isAnimating
                ? direction === 'next'
                  ? 'opacity-0 -translate-x-8'
                  : 'opacity-0 translate-x-8'
                : 'opacity-100 translate-x-0'
            }`}
          >
            <h2 className="font-['Playfair_Display'] text-2xl lg:text-3xl text-white text-center mb-10 leading-relaxed">
              {current.question}
            </h2>

            {/* Opções */}
            <div className="grid grid-cols-1 gap-4">
              {current.options.map((option, idx) => {
                const isSelected = selectedOption === option.profile;
                return (
                  <button
                    key={idx}
                    onClick={() => handleSelect(option.profile)}
                    className={`group relative w-full text-left px-6 py-5 rounded-xl border transition-all duration-300 cursor-pointer ${
                      isSelected
                        ? 'border-gold-500/60 bg-gold-500/10 shadow-[0_0_20px_rgba(201,168,76,0.1)]'
                        : 'border-white/8 bg-white/[0.02] hover:border-white/15 hover:bg-white/[0.04]'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 ${
                          isSelected
                            ? 'bg-gold-500/20 text-gold-400'
                            : 'bg-white/5 text-white/30 group-hover:text-white/50'
                        }`}
                      >
                        <i className={`${option.icon} text-lg`}></i>
                      </div>
                      <span
                        className={`font-['Montserrat'] text-sm tracking-wide transition-colors duration-300 ${
                          isSelected ? 'text-gold-300' : 'text-white/50 group-hover:text-white/70'
                        }`}
                      >
                        {option.text}
                      </span>
                      <div className="ml-auto">
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                            isSelected ? 'border-gold-500 bg-gold-500' : 'border-white/15'
                          }`}
                        >
                          {isSelected && <i className="ri-check-line text-xs text-luxury-black"></i>}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Navegação */}
          <div className="flex items-center justify-between mt-12">
            <button
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className={`font-['Montserrat'] text-xs uppercase tracking-wider px-6 py-3 rounded-lg border transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                currentIndex === 0
                  ? 'border-white/5 text-white/15 cursor-not-allowed'
                  : 'border-white/15 text-white/50 hover:border-gold-500/30 hover:text-gold-400'
              }`}
            >
              <i className="ri-arrow-left-s-line text-base"></i>
              Voltar
            </button>

            <button
              onClick={handleNext}
              disabled={!selectedOption}
              className={`font-['Montserrat'] text-xs uppercase tracking-wider px-8 py-3 rounded-lg transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                selectedOption
                  ? 'bg-gradient-to-r from-gold-500 to-gold-600 text-luxury-black hover:shadow-[0_0_20px_rgba(201,168,76,0.3)] hover:scale-105'
                  : 'bg-white/5 text-white/20 cursor-not-allowed'
              }`}
            >
              {currentIndex === questions.length - 1 ? 'Ver Resultado' : 'Próxima'}
              <i className={`${currentIndex === questions.length - 1 ? 'ri-sparkling-line' : 'ri-arrow-right-s-line'} text-base`}></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
